import { Core } from "@faire/design-tokens";
import { isRetailer } from "@faire/help-center-utils/serialized-data/getRetailer";
import { Button } from "@faire/web--source/slate/Button";
import { Information } from "@faire/web--source/slate/icons/foundation/Information";
import { Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackHelpCenterLearnMoreFromActionModalClick } from "@faire/web-api--source/events/helpCenter/click/learnMoreFromActionModal";
import { trackHelpCenterViewOrderDetailsFromActionModalClick } from "@faire/web-api--source/events/helpCenter/click/viewOrderDetailsFromActionModal";
import React, { type JSX } from "react";

import { useActionTypes } from "../hooks/useActionTypes";
import { useShowMessage } from "../hooks/useShowMessage";
import { QuickAction, QuickActionType } from "../types";

import { IMessageInfoProps } from "./types";

export const MessageInfo: React.FC<IMessageInfoProps> = ({
  selectedOption,
  dropdownOptions,
}) => {
  const { actionType } = useActionTypes();

  const {
    showEmptyState,
    showNotShippedMessage,
    showBeingFulfilledMessage,
    showNeedExtensionMessage,
    redirectLink,
  } = useShowMessage(dropdownOptions, selectedOption);

  if (showEmptyState) {
    if (actionType?.id === QuickActionType.RETURN_ITEMS) {
      return (
        <>
          <Spacer height="1x" />
          <Row align="center">
            <Information />
            <Spacer width="1x" />
            <Typography color={Core.text.subdued}>
              <StrictLocalMsg
                defaultMessage="Looks like you don’t have any orders available for return."
                description={{
                  text: "Message to show when there are no orders available for return in the dropdown of quick actions in the help center",
                  img: "https://cdn.faire.com/fastly/92128266a1c59ff6452572a1aa64d697c246dd41d060d70195779b6e4cc8a59b.png",
                }}
              />
            </Typography>
          </Row>
        </>
      );
    }

    if (actionType?.id === QuickActionType.CANCEL_AN_ORDER) {
      return (
        <>
          <Spacer height="1x" />
          <Row align="center">
            <Information />
            <Spacer width="1x" />
            <Typography color={Core.text.subdued}>
              <StrictLocalMsg
                defaultMessage="Looks like you don’t have any orders that can be cancelled."
                description={{
                  text: "Message to show when there are no orders that can be cancelled in the dropdown of quick actions in the help center",
                  img: "https://cdn.faire.com/fastly/92128266a1c59ff6452572a1aa64d697c246dd41d060d70195779b6e4cc8a59b.png",
                }}
              />
            </Typography>
          </Row>
        </>
      );
    }

    return (
      <>
        <Spacer height="1x" />
        <Row align="center">
          <Information />
          <Spacer width="1x" />
          <Typography color={Core.text.subdued}>
            {isRetailer() ? (
              <StrictLocalMsg defaultMessage="Looks like you haven’t placed any orders yet." />
            ) : (
              <StrictLocalMsg defaultMessage="Looks like you haven’t received any orders yet." />
            )}
          </Typography>
        </Row>
      </>
    );
  }

  if (showNotShippedMessage) {
    return (
      <>
        <Spacer height="1x" />
        <Row align="center">
          <Information />
          <Spacer width="1x" />
          <Typography color={Core.text.subdued}>
            <StrictLocalMsg
              defaultMessage="This order has not been shipped yet. <LinkFormatting>View Order Details</LinkFormatting>"
              values={{
                LinkFormatting: (chunks: JSX.Element) =>
                  ViewOrderDetailsFormatting(chunks, redirectLink, actionType),
              }}
            />
          </Typography>
        </Row>
      </>
    );
  }

  if (showBeingFulfilledMessage) {
    return (
      <>
        <Spacer height="1x" />
        <Row align="center">
          <Information />
          <Spacer width="1x" />
          <Typography color={Core.text.subdued}>
            {" "}
            <StrictLocalMsg
              defaultMessage="This order is being fulfilled, but you can still request a cancellation from the brand directly. <LinkFormatting>Learn More</LinkFormatting>"
              values={{
                LinkFormatting: (chunks: JSX.Element) =>
                  LearnMoreLinkFormatting(chunks, redirectLink, actionType),
              }}
            />
          </Typography>
        </Row>
      </>
    );
  }

  if (showNeedExtensionMessage) {
    return (
      <>
        <Spacer height="1x" />
        <Row align="center">
          <Information />
          <Spacer width="1x" />
          <Typography color={Core.text.subdued}>
            {" "}
            <StrictLocalMsg
              defaultMessage="This order is past our 14 day policy to report damaged or missing items. <LinkFormatting>Learn More</LinkFormatting>"
              values={{
                LinkFormatting: (chunks: JSX.Element) =>
                  LearnMoreLinkFormatting(chunks, redirectLink, actionType),
              }}
            />
          </Typography>
        </Row>
      </>
    );
  }

  return null;
};

const LearnMoreLinkFormatting = (
  chunks: JSX.Element,
  redirectLink: string,
  actionType: QuickAction | undefined
) => (
  <Button
    variant="tertiary"
    linkTo={redirectLink}
    onClick={() =>
      trackHelpCenterLearnMoreFromActionModalClick(actionType?.label ?? "")
    }
  >
    {chunks}
  </Button>
);

const ViewOrderDetailsFormatting = (
  chunks: JSX.Element,
  redirectLink: string,
  actionType: QuickAction | undefined
) => (
  <Button
    variant="tertiary"
    linkTo={redirectLink}
    onClick={() =>
      trackHelpCenterViewOrderDetailsFromActionModalClick(
        actionType?.label ?? ""
      )
    }
  >
    {chunks}
  </Button>
);
